import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Full-screen image lightbox.
 *  - images: [{id, name, data}] OR [string urls]
 *  - startIndex: which image to open first
 *  - open / onClose
 */
const ImageLightbox = ({ images, startIndex = 0, open, onClose }) => {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => {
    if (open) setIdx(startIndex);
  }, [open, startIndex]);

  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  if (!open || !images || images.length === 0) return null;

  const current = images[idx];
  const src = typeof current === "string" ? current : current?.data;
  const name = typeof current === "string" ? `Image ${idx + 1}` : (current?.name || `Image ${idx + 1}`);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white grid place-items-center transition"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        aria-label="Close"
      ><X className="w-5 h-5" /></button>

      {images.length > 1 && (
        <>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white grid place-items-center transition"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Previous"
          ><ChevronLeft className="w-6 h-6" /></button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white grid place-items-center transition"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Next"
          ><ChevronRight className="w-6 h-6" /></button>
        </>
      )}

      <figure className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={name}
          className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain bg-white"
          data-testid="lightbox-image"
        />
        <figcaption className="mt-3 text-white/85 text-sm flex items-center gap-3">
          <span className="truncate max-w-[60vw]">{name}</span>
          <span className="text-white/50">{idx + 1} / {images.length}</span>
          <a href={src} download={name} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[#9DCD3F] hover:text-white transition" data-testid="lightbox-download">
            <Download className="w-4 h-4" /> Download
          </a>
        </figcaption>
      </figure>
    </div>
  );
};

export default ImageLightbox;
